/**
 * Turning a tool result into something worth looking at.
 *
 * 🔴 BUILT FROM THE TOOL RESULT, NEVER FROM THE MODEL'S PROSE, AND THAT IS THE
 *    WHOLE DESIGN. The model already summarises the result in text. If a card
 *    were built from what it WROTE, a hallucinated price would appear inside a
 *    confident-looking box next to a real photograph, which is the worst
 *    possible version of this feature. The tool result is ground truth, it is
 *    already in the browser's hand, and it costs no extra tokens. Same
 *    principle as `verified` coming from the outcome and never from what the
 *    model said about it.
 *
 * 🔴 FIELD ALIASES, NOT A SCHEMA. Verb has no idea what a customer's API
 *    returns and must not require them to reshape it. So this looks for the
 *    names people actually use and gives up quietly on anything else, which is
 *    the difference between a feature that works on the first install and one
 *    that needs a migration first.
 *
 * 🔴 GIVING UP QUIETLY IS THE POINT. A result that does not clearly look like a
 *    list of things renders as it always did: the model's text answer, and
 *    nothing else. Guessing wrong here means drawing garbage on a customer's
 *    product, so the bar for "this is a card" is deliberately high.
 */

export interface Card {
  title: string;
  image?: string;
  price?: string;
  subtitle?: string;
  url?: string;
  rating?: string;
}

const IMAGE_KEYS = ["image", "image_url", "imageUrl", "thumbnail", "thumb", "photo", "picture", "img"];
const TITLE_KEYS = ["title", "name", "label", "product_name", "productName", "heading"];
const PRICE_KEYS = ["price", "amount", "cost", "total", "price_formatted", "displayPrice"];
const SUB_KEYS = ["description", "subtitle", "summary", "excerpt", "status", "category"];
const URL_KEYS = ["url", "link", "href", "permalink", "product_url", "productUrl"];
const RATING_KEYS = ["rating", "stars", "score", "review_score", "averageRating"];

/** Where a list usually hides when the result is an object. */
const LIST_KEYS = ["items", "data", "results", "products", "rows", "records", "list", "orders"];

/**
 * 🔴 EIGHT. A tool that returns four hundred rows must not paint four hundred
 *    cards into somebody's chat panel: the model's summary is the answer, and
 *    the cards are there to make the first few concrete. The text answer still
 *    describes the whole result.
 */
const MAX_CARDS = 8;

function firstString(row: Record<string, unknown>, keys: readonly string[]): string | undefined {
  for (const k of keys) {
    const v = row[k];
    if (typeof v === "string" && v.trim()) return v.trim();
    // A price is very often a number, and a rating almost always is.
    if (typeof v === "number" && Number.isFinite(v)) return String(v);
  }
  return undefined;
}

/**
 * 🔴 THE ONE SECURITY-CRITICAL FUNCTION IN THIS FILE. These URLs come from a
 *    customer's API, which may be echoing something a user of theirs typed, and
 *    they end up in an `src` and an `href` on that customer's own page.
 *
 *    `javascript:` in an href is script execution on their origin. `data:` can
 *    carry an SVG, and an SVG can carry a script. `blob:` and `file:` have no
 *    business here either. So this is an ALLOWLIST of two schemes, not a
 *    blocklist of the ones anybody thought of.
 *
 *    http is permitted only on localhost, so a founder testing against their
 *    own dev server sees their images, and nobody else is silently downgraded
 *    to an insecure request on a live page.
 */
export function safeUrl(raw: string | undefined, base: string): string | undefined {
  if (!raw) return undefined;
  try {
    const u = new URL(raw, base);
    if (u.protocol === "https:") return u.href;
    if (u.protocol === "http:" && /^(localhost|127\.0\.0\.1|\[::1\])$/.test(u.hostname)) {
      return u.href;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/** Pulls the array out of whatever the tool actually returned. */
function asRows(result: unknown): Record<string, unknown>[] | null {
  if (Array.isArray(result)) return result.filter(isRow);
  if (!result || typeof result !== "object") return null;

  const obj = result as Record<string, unknown>;
  for (const key of LIST_KEYS) {
    if (Array.isArray(obj[key])) return (obj[key] as unknown[]).filter(isRow);
  }
  // A single object that is itself card-shaped: one order, one product.
  return isRow(obj) ? [obj] : null;
}

function isRow(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/**
 * @param base the page's own URL, so a relative image path resolves against the
 *        customer's origin rather than ours.
 */
export function detectCards(result: unknown, base: string): Card[] | null {
  const rows = asRows(result);
  if (!rows || rows.length === 0) return null;

  const cards: Card[] = [];
  for (const row of rows.slice(0, MAX_CARDS)) {
    const title = firstString(row, TITLE_KEYS);
    if (!title) return null;

    const image = safeUrl(firstString(row, IMAGE_KEYS), base);
    const price = firstString(row, PRICE_KEYS);

    /**
     * 🔴 A TITLE ALONE IS NOT A CARD. Almost every API result has a `name`
     *    somewhere, so accepting that would turn every list the assistant ever
     *    reads into a wall of boxes. A picture or a price is what says somebody
     *    meant this to be looked at rather than summarised.
     */
    if (!image && !price) return null;

    cards.push({
      title,
      ...(image ? { image } : {}),
      ...(price ? { price } : {}),
      ...(firstString(row, SUB_KEYS) ? { subtitle: firstString(row, SUB_KEYS)! } : {}),
      ...(safeUrl(firstString(row, URL_KEYS), base)
        ? { url: safeUrl(firstString(row, URL_KEYS), base)! }
        : {}),
      ...(firstString(row, RATING_KEYS) ? { rating: firstString(row, RATING_KEYS)! } : {}),
    });
  }

  // 🔴 ALL OR NOTHING. A partly-rendered list, three cards and a silent gap, is
  //    worse than none: the reader cannot tell the difference between "there
  //    were three" and "we drew three of the eight".
  return cards.length === rows.slice(0, MAX_CARDS).length ? cards : null;
}
